import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField, Box, useMediaQuery, useTheme,
  Typography
} from "@mui/material";
import Footer from '../components/SFooter';
import Header from '../components/Header';
import BackToHome from "../components/BackToHome";
import { saveMyReferral, getMyReferrals } from '../service/referralService';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { normalizeKey } from '../i18n';


  
const MyReferrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
       const { user } = useAuth();
const { t } = useTranslation();
  const {
    email,
    name,
    userId,
    roles
  } = user || {};
  
   const theme = useTheme();

  // Breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));   // <600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600-900px
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));   // >900px

  // Adjust settings per device
  const height = isMobile ? 350 : isTablet ? 450 : 600;
  const pageSize = isMobile ? 5 : isTablet ? 10 : 25;
  const rowsPerPageOptions = isMobile ? [5, 10] : [10, 25, 50];


  // form state for new referral
  const [formData, setFormData] = useState({
    friendName: "",
    friendEmail: "",
    friendPhone: "",
    referrerId: userId
  });

  const [openForm, setOpenForm] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);

  // fetch user's referrals
  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const res = await getMyReferrals(userId); // only logged-in user's referrals
      setReferrals(res.data);
    } catch (err) {
      console.error("Error fetching referrals", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  // form input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // save referral (user can only create, not edit)
  const handleSave = async () => {
    try {
      await saveMyReferral(formData);
      fetchReferrals();
      setOpenForm(false);
      setFormData({ friendName: "", friendEmail: "", friendPhone: "", referrerId: userId });
    } catch (err) {
     if (err.response) {
      if (err.response.status === 409) {
        setErrorMessage("This referral already exists. Please try a different email/phone.");
      } else {
        setErrorMessage(err.response.data.message || "Something went wrong.");
      }
    } else {
      setErrorMessage("Network error. Please try again.");
    }
      console.error("Error saving referral", err);
    }
  };

  // open details
  const handleView = (referral) => {
    setSelectedReferral(referral);
    setOpenDetails(true);
  };

  // columns for DataGrid
  const columns = [
    { field: "referralId", headerName: "ID", width: 90 },
    { field: "friendName", headerName: "Friend Name", flex: 1 },
    { field: "friendEmail", headerName: "Email", flex: 1 },
    { field: "friendPhone", headerName: "Phone", flex: 1 },
    {
      field: "bonusAwarded",
      headerName: "Bonus Awarded",
      width: 150,
      renderCell: (params) => (params.value ? "Yes" : "No"),
    },
    { field: "referralStatus", headerName: "Referral Status", width: 150 },
    { field: "createdAt", headerName: "Created At", width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => handleView(params.row)}
        >
          {t('view', 'View')}
        </Button>
      ),
    },
  ];


  const visibleColumns = isMobile
    ? columns.filter((col) => ["friendName", "referralStatus", "actions"].includes(col.field)) // keep minimal
    : columns;

  return (
            <>
      <Header />
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">
    {t('my_referrals', 'My Referrals')} 
    </h1>

      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpenForm(true)}
        style={{ marginBottom: "1rem" }}
      >
         {t('create_referral', 'Create Referral')}  
      </Button>
<Box
      sx={{
        height: height,
        width: "100%",
        "& .MuiDataGrid-root": {
          fontSize: isMobile ? "0.75rem" : "0.9rem",
        },
      }}
    >
      <DataGrid
        rows={referrals}
        columns={visibleColumns}
        getRowId={(row) => row.referralId}
        pageSize={pageSize}
        rowsPerPageOptions={rowsPerPageOptions}
        loading={loading}
        components={{ Toolbar: GridToolbar }}
        disableSelectionOnClick
        autoHeight={false}
      />
    </Box>

      {/* Form Dialog */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle> {t('create_referral', 'Create Referral')} </DialogTitle>
        <DialogContent>
             {errorMessage && (
    <p style={{ color: "red", marginBottom: "0.5rem" }}>{errorMessage}</p>
  )}
  
          <TextField
            margin="dense"
            label="Friend Name"
            name="friendName"
            fullWidth
            value={formData.friendName}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            label="Friend Email"
            name="friendEmail"
            type="email"
            fullWidth
            value={formData.friendEmail}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            label="Friend Phone"
            name="friendPhone"
            fullWidth
            value={formData.friendPhone}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {t('save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Referral Details</DialogTitle>
        <DialogContent dividers>
          {selectedReferral && (
            <div className="space-y-2">
              <p>
                <strong>ID:</strong> {selectedReferral.referralId}
              </p>
              <p>
                <strong>Friend Name:</strong> {selectedReferral.friendName}
              </p>
              <p>
                <strong>Email:</strong> {selectedReferral.friendEmail}
              </p>
              <p>
                <strong>Phone:</strong> {selectedReferral.friendPhone}
              </p>
              <p>
                <strong>Bonus Awarded:</strong>{" "}
                {selectedReferral.bonusAwarded ? "Yes" : "No"}
              </p>
              <p>
                <strong>Referral Status:</strong> {selectedReferral.referralStatus}
              </p>
              <p>
                <strong>Created At:</strong> {selectedReferral.createdAt}
              </p>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}> {t('close', 'Close')} </Button>
        </DialogActions>
      </Dialog>
      <BackToHome />

    </div>

       <Footer />
    </>
  );
};

export default MyReferrals;
